import {useCallback, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AppFileInputButton from '../../../../components/common/AppFileInputButton';
import {SVG} from '../../../../constants/assetLists';
import type {FormQuestion} from '../../../../services/operations/forms';
import {
  buildPublicFileUploadInputs,
  fileMatchesAllowedTypes,
} from '../../fileUploadAnswer';
import FilePreview from '../../../forms/components/AddEditForm/components/QuestionsTab/FilePreview';

type Props = {
  question: FormQuestion;
  value: File[] | undefined;
  onChange: (files: File[]) => void;
};

function PublicFileUpload({question, value, onChange}: Props) {
  const {t} = useTranslation();
  const files = value ?? [];
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const allowedTypes = question.fileUploadConfig?.allowedTypes ?? [];
  const maxFiles = question.fileUploadConfig?.maxFiles ?? 1;
  const maxFileSizeMB = question.fileUploadConfig?.maxFileSizeMB ?? 10;

  const {showCamera, showDocuments, cameraAccept, documentsAccept} = useMemo(
    () => buildPublicFileUploadInputs(allowedTypes as string[]),
    [allowedTypes],
  );

  const [localError, setLocalError] = useState<string | null>(null);

  const atMax = files.length >= maxFiles;
  const maxBytes = maxFileSizeMB * 1024 * 1024;

  const mergeIncoming = useCallback(
    (incoming: File[]) => {
      setLocalError(null);

      if (incoming.length === 0) return;

      if (atMax) {
        setLocalError(t('forms.public.fileUpload.errors.maxReached'));
        return;
      }

      const fileNames = new Set(files.map(f => f.name + f.size));
      const uniqueNew = incoming.filter(f => !fileNames.has(f.name + f.size));
      if (uniqueNew.length === 0) return;

      for (const f of uniqueNew) {
        if (!fileMatchesAllowedTypes(f, allowedTypes as string[])) {
          setLocalError(t('forms.public.fileUpload.errors.invalidType'));
          return;
        }
        if (f.size > maxBytes) {
          setLocalError(
            t('forms.public.fileUpload.errors.tooLarge', {max: maxFileSizeMB}),
          );
          return;
        }
      }

      const room = maxFiles - files.length;
      const toAdd = uniqueNew.slice(0, room);
      if (uniqueNew.length > room) {
        setLocalError(t('forms.public.fileUpload.errors.maxReached'));
      }

      onChange([...files, ...toAdd]);

      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
      });
    },
    [
      allowedTypes,
      atMax,
      files,
      maxBytes,
      maxFileSizeMB,
      maxFiles,
      onChange,
      t,
    ],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    // Snapshot before clearing input — clearing can empty the live FileList in some browsers.
    const snapshot = Array.from(list);
    e.target.value = '';
    mergeIncoming(snapshot);
  };

  const handleFileRemove = (idx: number) => {
    setLocalError(null);
    onChange(files.filter((_, i) => i !== idx));
  };

  const qid = question._id;

  return (
    <div ref={sectionRef} className="flex flex-col gap-4 mt-3">
      <div className="flex items-center gap-2 flex-wrap">
        {showCamera && (
          <AppFileInputButton
            id={`public-file-${qid}-media`}
            icon={SVG.cameraIcon}
            accept={cameraAccept || 'image/*,video/*'}
            multiple={maxFiles > 1}
            disabled={atMax}
            onChange={handleFileChange}
          />
        )}
        {showDocuments && (
          <AppFileInputButton
            id={`public-file-${qid}-docs`}
            icon={SVG.fileIcon}
            accept={documentsAccept}
            multiple={maxFiles > 1}
            disabled={atMax}
            onChange={handleFileChange}
          />
        )}
      </div>

      {localError && (
        <p className="m-0 text-[13px] text-raspberryRed">{localError}</p>
      )}

      <div className="flex flex-col gap-2">
        {files.map((item, idx) => (
          <FilePreview
            key={`${item.name}-${item.size}-${idx}`}
            file={item}
            onRemove={() => handleFileRemove(idx)}
          />
        ))}
      </div>
    </div>
  );
}

export default PublicFileUpload;
